import { WaterlilyClient, createQueryKeys } from '@/api/waterlily';

export const wizardEvalsKeys = {
  ...createQueryKeys('wizardEvals'),
  allTestCases: () => ['wizardEvals', 'testCases'] as const,
  testCases: (params?: { carrier?: string }) =>
    ['wizardEvals', 'testCases', { params: params ?? {} }] as const,
  testCaseDetail: (testCaseId: string) =>
    ['wizardEvals', 'testCases', 'detail', testCaseId] as const,
  allRules: () => ['wizardEvals', 'rules'] as const,
  rules: (params?: { carrier?: string }) =>
    ['wizardEvals', 'rules', { params: params ?? {} }] as const,
  runs: () => ['wizardEvals', 'runs'] as const,
  runDetail: (runId: string) => ['wizardEvals', 'runs', 'detail', runId] as const,
  runsByTestCase: (testCaseId: string) =>
    ['wizardEvals', 'runs', 'byTestCase', testCaseId] as const,
  runResults: () => ['wizardEvals', 'runResults'] as const,
  intakePayload: (clientId: string) =>
    ['wizardEvals', 'intakePayload', clientId] as const,
};

export interface WizardEvalTestCase {
  testCaseId: string;
  name: string;
  description: string;
  carrier: string;
  promptType: string;
  intakePayload: unknown;
  isJoint: boolean;
  expectedOutcome: string;
  expectedRateClass: string | null;
  expectedRuleIds: string[];
  unexpectedRuleIds: string[];
  sourceClientId: string | null;
  createdBy: string;
  createdDateTime: string;
  updatedDateTime: string;
}

export interface CreateTestCaseRequest {
  name: string;
  description?: string;
  carrier: string;
  promptType: string;
  intakePayload: unknown;
  isJoint: boolean;
  expectedOutcome: string;
  expectedRateClass?: string | null;
  expectedRuleIds: string[];
  unexpectedRuleIds: string[];
  sourceClientId?: string | null;
}

export type UpdateTestCaseRequest = Partial<
  Omit<CreateTestCaseRequest, 'intakePayload' | 'sourceClientId'>
>;

export interface ManualOverrides {
  fpReclassified?: string[];
  fpIgnored?: string[];
  fnAccepted?: string[];
  fnIgnored?: string[];
  note?: string;
  reviewedBy?: string;
  reviewedAt?: string;
}

export interface WizardEvalRunResult {
  runResultId: string;
  runId: string;
  testCaseId: string;
  outcomeMatch: boolean;
  rateClassMatch: boolean;
  actualOutcome: string | null;
  actualRateClass: string | null;
  expectedOutcome: string | null;
  expectedRateClass: string | null;
  rulePrecision: number | null;
  ruleRecall: number | null;
  ruleF1: number | null;
  truePositives: string[];
  falsePositives: string[];
  falseNegatives: string[];
  effectiveTruePositives: string[];
  effectiveFalsePositives: string[];
  effectiveFalseNegatives: string[];
  effectivePrecision: number;
  effectiveRecall: number;
  effectiveF1: number;
  hasOverrides: boolean;
  manualOverrides: ManualOverrides | null;
  assessorOutput: unknown;
  reviewerResult: unknown;
  reviewStatus: string | null;
  createdDateTime: string;
}

export interface WizardEvalRun {
  runId: string;
  scope: string;
  status: string;
  selectedTestCaseIds: string[];
  modelVersion: string | null;
  promptVersion: string | null;
  metadata: Record<string, unknown> | null;
  aggregateMetrics: unknown;
  errorMessage: string | null;
  triggeredBy: string;
  startedAt: string | null;
  completedAt: string | null;
  createdDateTime: string;
}

export interface WizardEvalRunResultRow {
  result: WizardEvalRunResult;
  testCase: {
    testCaseId: string;
    name: string;
    carrier: string;
    promptType: string;
  } | null;
  run: {
    runId: string;
    scope: string;
    status: string;
    modelVersion: string | null;
    promptVersion: string | null;
    metadata: Record<string, unknown> | null;
    triggeredBy: string;
    createdDateTime: string;
    completedAt: string | null;
  } | null;
}

export interface RunResultsResponse {
  rows: WizardEvalRunResultRow[];
}

export interface TriggerSingleRunResponse {
  run: WizardEvalRun;
}

export interface RunDetailResponse {
  run: WizardEvalRun;
  results: WizardEvalRunResult[];
  liveAggregate: {
    totalCases: number;
    completedCases: number;
    failedCases: number;
    outcomeAccuracy: number;
    rateClassAccuracy: number;
    macroF1: number;
  };
}

export interface RunsByTestCaseRow {
  run: WizardEvalRun;
  result: WizardEvalRunResult | null;
}

export interface RunsByTestCaseResponse {
  runs: RunsByTestCaseRow[];
}

export interface IntakePayloadResponse {
  typeformPayload: unknown;
}

export interface WizardEvalRule {
  ruleId: string;
  carrier: string;
  category: string | null;
  ruleText: string;
  keywords: string[];
  isActive: boolean;
  createdBy: string;
  createdDateTime: string;
  updatedDateTime: string;
}

export interface CreateRuleRequest {
  ruleId: string;
  carrier: string;
  category: string | null;
  ruleText: string;
  keywords: string[];
  isActive: boolean;
}

export type UpdateRuleRequest = Partial<Omit<CreateRuleRequest, 'ruleId'>>;

export const wizardEvalsApi = {
  listTestCases: (params?: { carrier?: string }) =>
    WaterlilyClient.get<WizardEvalTestCase[]>(
      '/wizard-underwriting-eval-test-cases',
      params,
    ),

  createTestCase: (data: CreateTestCaseRequest) =>
    WaterlilyClient.post<WizardEvalTestCase>(
      '/wizard-underwriting-eval-test-case',
      data,
    ),

  updateTestCase: (testCaseId: string, data: UpdateTestCaseRequest) =>
    WaterlilyClient.put<WizardEvalTestCase>(
      `/wizard-underwriting-eval-test-case/${testCaseId}`,
      data,
    ),

  deleteTestCase: (testCaseId: string) =>
    WaterlilyClient.delete<{ success: boolean }>(
      `/wizard-underwriting-eval-test-case/${testCaseId}`,
    ),

  runSingleTestCase: (testCaseId: string) =>
    WaterlilyClient.post<TriggerSingleRunResponse>(
      `/wizard-underwriting-eval-single-run/${testCaseId}`,
      {},
    ),

  fetchRunByRunId: (runId: string) =>
    WaterlilyClient.get<RunDetailResponse>(
      `/wizard-underwriting-eval-run/${runId}`,
    ),

  fetchRunsByTestCaseId: (testCaseId: string) =>
    WaterlilyClient.get<RunsByTestCaseResponse>(
      `/wizard-underwriting-eval-runs/${testCaseId}`,
    ),

  listRunResults: () =>
    WaterlilyClient.get<RunResultsResponse>(
      '/wizard-underwriting-eval-run-results',
    ),

  fetchIntakePayload: (clientId: string) =>
    WaterlilyClient.get<IntakePayloadResponse>(
      `/wizard-intake-payload/${clientId}`,
    ),

  listRules: (params?: { carrier?: string }) =>
    WaterlilyClient.get<WizardEvalRule[]>(
      '/wizard-underwriting-eval-rules',
      params,
    ),

  importRules: (rules: ImportRulePayload[]) =>
    WaterlilyClient.post<ImportRulesResponse>(
      '/wizard-underwriting-eval-rule-import',
      { rules },
    ),

  createRule: (data: CreateRuleRequest) =>
    WaterlilyClient.post<WizardEvalRule>('/wizard-underwriting-eval-rule', data),

  updateRule: (ruleId: string, data: UpdateRuleRequest) =>
    WaterlilyClient.put<WizardEvalRule>(
      `/wizard-underwriting-eval-rule/${ruleId}`,
      data,
    ),

  deleteRule: (ruleId: string) =>
    WaterlilyClient.delete<{ success: boolean }>(
      `/wizard-underwriting-eval-rule/${ruleId}`,
    ),

  updateRunResultOverrides: (
    runResultId: string,
    overrides: Pick<
      ManualOverrides,
      'fpReclassified' | 'fpIgnored' | 'fnAccepted' | 'fnIgnored' | 'note'
    >,
  ) =>
    WaterlilyClient.put<WizardEvalRunResult>(
      `/wizard-underwriting-eval-run-result-overrides/${runResultId}`,
      overrides,
    ),
};

export interface ImportRulePayload {
  ruleId: string;
  carrier: string;
  category: string | null;
  ruleText: string;
  keywords: string[];
  isActive: boolean;
}

export interface ImportRulesResponse {
  added: number;
  updated: number;
  deactivated: number;
  deactivatedRuleIds: string[];
}
